import { z } from 'zod'
import { initContract } from '@ts-rest/core'
import { ErrorResponseSchema } from './general'
import { EnrichedOrderSchema, UpdateOrderDeliveryInfoSchema } from '@repo/api-types'
import { DeliverySlotWindow, OrderResponseSchema } from '@lyka-pet-food/contracts'

const ResolvedDeliveryDateSchema = z.object({
  date: z.string(),
  slotId: z.nativeEnum(DeliverySlotWindow),
})

const OrderIdSchema = z.object({
  orderId: z.string().uuid(),
})

const EnrichedOrderResponseSchema = z.object({
  pastOrders: z.array(EnrichedOrderSchema),
  currentOrders: z.array(EnrichedOrderSchema),
  futureOrders: z.array(EnrichedOrderSchema),
  failedOrders: z.array(EnrichedOrderSchema),
  nextAvailableOrderDate: z.string().nullable(),
  nextAvailableOrderBillingDate: z.string().nullable(),
  nextAvailableOrderSlot: z.string().nullable(),
})

const GetPastOrdersQuerySchema = z.object({
  page: z.coerce.number().int().nonnegative().optional().default(0),
})

const GetPastOrdersResponseSchema = z.object({
  data: z.array(EnrichedOrderSchema),
  hasNext: z.boolean(),
})

const AddOneOffExtrasBodySchema = z.object({
  subscriptionId: z.string(),
  extras: z.object({
    productVariantId: z.string(),
    quantity: z.number(),
  }).array().min(1),
  deleteSubscriptionExtras: z.boolean().optional(),
})

const UpdateOneOffExtraBodySchema = z.object({
  subscriptionId: z.string(),
  orderLineItemId: z.string(),
  quantity: z.number(),
})

const DeleteOneOffExtraBodySchema = z.object({
  subscriptionId: z.string(),
  orderLineItemId: z.string(),
})

const OneOffExtrasResponseSchema = z.object({
  order: OrderResponseSchema,
})

const GetDelayedDeliveryDateQuerySchema = z.object({
  weeks: z.coerce.number().int().min(1).max(2),
})

const c = initContract()

export const orderContract = c.router(
  {
    getOrders: {
      method: 'GET',
      path: '/orders',
      responses: {
        200: EnrichedOrderResponseSchema,
      },
    },
    getPastOrders: {
      method: 'GET',
      path: '/orders/past',
      query: GetPastOrdersQuerySchema,
      responses: {
        200: GetPastOrdersResponseSchema,
      },
    },
    getOrderById: {
      method: 'GET',
      path: '/orders/:orderId',
      pathParams: OrderIdSchema,
      responses: {
        200: EnrichedOrderSchema,
      },
    },
    addOneOffExtras: {
      method: 'POST',
      path: '/order/:orderId/add-extra',
      pathParams: OrderIdSchema,
      body: AddOneOffExtrasBodySchema,
      responses: {
        200: OneOffExtrasResponseSchema
      },
    },
    updateOneOffExtra: {
      method: 'POST',
      path: '/order/:orderId/update-extra',
      pathParams: OrderIdSchema,
      body: UpdateOneOffExtraBodySchema,
      responses: {
        200: OneOffExtrasResponseSchema,
      },
    },
    deleteOneOffExtra: {
      method: 'POST',
      path: '/order/:orderId/delete-extra',
      pathParams: OrderIdSchema,
      body: DeleteOneOffExtraBodySchema,
      responses: {
        200: OneOffExtrasResponseSchema,
      },
    },
    updateOrderDeliveryInfo: {
      method: 'POST',
      path: '/orders/:orderId/delivery-info',
      body: UpdateOrderDeliveryInfoSchema,
      pathParams: OrderIdSchema,
      responses: {
        200: OrderIdSchema,
      },
    },
    getDelayedDeliveryDate: {
      method: 'GET',
      path: '/orders/:orderId/delayed-delivery-date',
      pathParams: OrderIdSchema,
      query: GetDelayedDeliveryDateQuerySchema,
      responses: {
        200: ResolvedDeliveryDateSchema,
      },
    },
    getNextAvailableDeliveryDate: {
      method: 'GET',
      path: '/orders/:orderId/next-available-delivery-date',
      pathParams: OrderIdSchema,
      responses: {
        200: ResolvedDeliveryDateSchema.nullable(),
      },
    },
  },
  {
    commonResponses: {
      400: ErrorResponseSchema,
      401: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
)
